import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { COHORT_FOR_ADMIN_LISTS_ATTRS } from './CohortAttrs';
import { type CohortIndexResponse, type CohortForAdminListsResponse } from './CohortIndex.types';
import { type CohortForAdminListsAttrs } from './CohortAttrs.types';
import { type AssessmentEditingLockDirection, type AssessmentEditingLockState } from './Cohort.types';

export const cohortsApi = createApi('CohortsApi', {
    ...getBackRoyalApiBaseConfig('CohortsApi'),
    endpoints: builder => ({
        getCohortsForAdmin: builder.query<CohortForAdminListsAttrs[], void>({
            query: () => ({
                url: 'cohorts.json',
                params: { 'fields[]': COHORT_FOR_ADMIN_LISTS_ATTRS },
            }),
            transformResponse: (response: CohortForAdminListsResponse) =>
                response.contents.cohorts.sort((a, b) => (a.startDate < b.startDate ? -1 : 1)),
        }),

        toggleAssessmentEditingLock: builder.mutation<
            AssessmentEditingLockState,
            { id: string; direction: AssessmentEditingLockDirection }
        >({
            query: ({ id, direction }) => ({
                method: 'PUT',
                url: `cohorts/${direction}_assessment_editing_lock.json`,
                body: { id },

                /*
                    A falsy timeout attaches no timeout signal, which opts out of the api's shared 30s
                    default, as the Angular $http path did. The toggle republishes the cohort and requeues
                    refund entitlements for every included student, so it runs much longer than a read.
                */
                timeout: 0,
            }),
            /*
                The endpoint answers with the whole ADMIN_FIELDS cohort, so narrow it here. Passing the
                response through would put the curriculum, the learner projects and the internal notes in
                the store and hand them to the caller.
            */
            transformResponse: (response: CohortIndexResponse<AssessmentEditingLockState>) => {
                const cohort = response?.contents?.cohorts?.[0];
                if (!cohort) throw new Error('Unexpected response shape from the assessment editing lock toggle');

                return {
                    assessmentEditingLocked: cohort.assessmentEditingLocked,
                    avgNumAssessmentsPerSpecialization: cohort.avgNumAssessmentsPerSpecialization,
                    avgNumExamsPerSpecialization: cohort.avgNumExamsPerSpecialization,
                    updatedAt: cohort.updatedAt,
                };
            },

            /*
                The shared base query retries once on a disconnect or a 502/504, and RTK applies that to
                mutations too. The toggle republishes the cohort, so a retry would publish a second
                version. The Angular $http path never retried.
            */
            extraOptions: { maxRetries: 0 },
        }),
    }),
});

export default cohortsApi;
